// components/process-units/Dehydration.js
import React, { useState } from 'react';

const Dehydration = ({ config, updateConfig }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Handle input changes
  const handleInputChange = (field, value) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      updateConfig({
        [field]: numValue
      });
    }
  };

  return (
    <div className="process-unit-card">
      <div 
        className="unit-header" 
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3>Dehydration</h3>
        <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
          {isExpanded ? '▼' : '▶'}
        </span>
      </div>
      
      {isExpanded && (
        <div className="unit-content">
          <p className="unit-description">
            Dehydration removes water from natural gas to prevent hydrate formation, corrosion, and
            to meet pipeline specifications. Common technologies include glycol absorption (TEG),
            solid desiccant adsorption (molecular sieves), or membrane separation.
          </p>
          
          <div className="parameter-group">
            <div className="parameter-row">
              <label>Dehydration Method</label>
              <select 
                value={config.dehydrationMethod || 'glycol'}
                onChange={(e) => updateConfig({ dehydrationMethod: e.target.value })}
              >
                <option value="glycol">Glycol Absorption (TEG)</option>
                <option value="molecularSieve">Molecular Sieve</option>
                <option value="silicaGel">Silica Gel</option>
                <option value="membranes">Membranes</option>
              </select>
            </div>
            
            <div className="parameter-row">
              <label>Inlet Water Content (lb/MMscf)</label>
              <input
                type="number"
                min="0"
                max="1000"
                step="1"
                value={config.inletWaterContent || 120}
                onChange={(e) => handleInputChange('inletWaterContent', e.target.value)}
              />
            </div>
            
            <div className="parameter-row">
              <label>Outlet Water Spec (lb/MMscf)</label>
              <input
                type="number"
                min="0"
                max="10"
                step="0.1"
                value={config.outletWaterSpec || 4}
                onChange={(e) => handleInputChange('outletWaterSpec', e.target.value)}
              />
            </div>
            
            {config.dehydrationMethod === 'glycol' && (
              <>
                <div className="parameter-row">
                  <label>Glycol Type</label>
                  <select 
                    value={config.glycolType || 'teg'}
                    onChange={(e) => updateConfig({ glycolType: e.target.value })}
                  >
                    <option value="teg">Triethylene Glycol (TEG)</option>
                    <option value="deg">Diethylene Glycol (DEG)</option>
                    <option value="meg">Monoethylene Glycol (MEG)</option>
                  </select>
                </div>
                
                <div className="parameter-row">
                  <label>Glycol Circulation Rate (L/kg H₂O)</label>
                  <input
                    type="number"
                    min="10"
                    max="100"
                    step="1"
                    value={config.glycolRate || 25}
                    onChange={(e) => handleInputChange('glycolRate', e.target.value)}
                  />
                </div>
                
                <div className="parameter-row">
                  <label>Regeneration Method</label>
                  <select 
                    value={config.regenerationMethod || 'standard'}
                    onChange={(e) => updateConfig({ regenerationMethod: e.target.value })}
                  >
                    <option value="standard">Standard (With Reflux)</option>
                    <option value="stripping">Gas Stripping</option>
                    <option value="vacuum">Vacuum Regeneration</option>
                  </select>
                </div>
                
                <div className="parameter-row">
                  <label>Reboiler Temperature (°C)</label>
                  <input
                    type="number"
                    min="150"
                    max="220"
                    step="1"
                    value={config.reboilerTemp || 204}
                    onChange={(e) => handleInputChange('reboilerTemp', e.target.value)}
                  />
                </div>
              </>
            )}
            
            {config.dehydrationMethod === 'molecularSieve' && (
              <>
                <div className="parameter-row">
                  <label>Number of Beds</label>
                  <input
                    type="number"
                    min="2"
                    max="6"
                    step="1"
                    value={config.bedCount || 3}
                    onChange={(e) => handleInputChange('bedCount', e.target.value)}
                  />
                </div>
                
                <div className="parameter-row">
                  <label>Regeneration Temperature (°C)</label>
                  <input
                    type="number"
                    min="200"
                    max="350"
                    step="10"
                    value={config.regenerationTemp || 290}
                    onChange={(e) => handleInputChange('regenerationTemp', e.target.value)}
                  />
                </div>
                
                <div className="parameter-row">
                  <label>Regeneration Heating Method</label>
                  <select 
                    value={config.regenHeatingMethod || 'gasHeating'}
                    onChange={(e) => updateConfig({ regenHeatingMethod: e.target.value })}
                  >
                    <option value="gasHeating">Slip Stream Gas</option>
                    <option value="electric">Electric Heater</option>
                    <option value="steam">Steam</option>
                  </select>
                </div>
              </>
            )}
            
            <div className="parameter-row">
              <label>Pressure Drop (bar)</label>
              <input
                type="number"
                min="0"
                max="5"
                step="0.1"
                value={config.pressureDrop || 0.5}
                onChange={(e) => handleInputChange('pressureDrop', e.target.value)}
              />
            </div>
          </div>
          
          <div className="unit-info-box">
            <h4>Engineering Notes</h4>
            <ul>
              <li>TEG systems typically achieve 5-10 lb/MMscf water content and are lower cost.</li>
              <li>Molecular sieves can achieve very low water content (&lt;1 lb/MMscf) but have higher capital and energy costs.</li>
              <li>Glycol regeneration is the major energy consumer in TEG systems, primarily in the reboiler.</li>
              <li>Molecular sieve regeneration requires heat for desorption plus cooling for the next adsorption cycle.</li>
              <li>Stripping gas can improve glycol regeneration, achieving lower water content with the same energy input.</li>
              <li>Pressure drop across dehydration units affects downstream compression requirements.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dehydration;